/**
 * @module filter.model
 *
 * pure types for the map filter registry. kept runes-free so it can be
 * imported from anywhere (controller, renderer, summary, tests) without
 * dragging a `.svelte.ts` runtime dependency along.
 *
 * the {@link FilterSummary} types below describe a reactive aggregate of
 * the panel — used by chips/badges that show "how many filters are active"
 * without exposing the full panel. the store itself (runes-based) lives in
 * `filter.svelte.ts`.
 *
 * authoring guide for the registry itself lives at the top of
 * `filter.config.svelte.ts`.
 */

/** identifier for a top-level tab in the filter panel */
export type FilterTab = 'pop' | 'lot'

/**
 * canonical identifiers for every `FilterDimension`. these strings double as
 * BE persistence keys AND field names on `FilterState` (in `filter.svelte.ts`),
 * so renaming a value breaks saved preferences and forces a matching field
 * rename — TS will flag every read site. import these constants instead of
 * inlining the string anywhere in the registry, the state container, or
 * consumers.
 */
export const FilterId = {
	POP_VISIBILITY_EXTERNAL: 'popVisibility.external',
	POP_VISIBILITY_INTERNAL: 'popVisibility.internal',
	POP_MAINTENANCE: 'popMaintenance',
	POP_TYPES: 'popTypes',
	LOT_TYPES: 'lotTypes',
	LOT_STATES: 'lotStates',
	SHOW_OWN_PROJECTS: 'showOwnProjects',
	SHOW_PUBLISHED: 'showPublished',
	SHOW_REQUESTED_BY_MM: 'showRequestedByMM',
	GROUP_LOTS: 'groupLots'
} as const

export type FilterId = (typeof FilterId)[keyof typeof FilterId]

/**
 * canonical keys for every {@link GroupDescriptor}. groups are visual
 * sections inside a tab; the key is referenced by `BaseDimension.group` so
 * the renderer can resolve "this dimension lives in that section". import
 * these constants instead of inlining the dotted strings.
 */
export const GroupKey = {
	POP_CLASSIFICATION: 'pop.classification',
	POP_STATE: 'pop.state',
	POP_TYPE: 'pop.type',
	LOT_TYPE: 'lot.type',
	LOT_PROJECTS: 'lot.projects',
	LOT_STATE: 'lot.state',
	LOT_GROUPING: 'lot.grouping'
} as const

export type GroupKey = (typeof GroupKey)[keyof typeof GroupKey]

/**
 * named subsets supported by the `lotStates` dimension's split `allToggle`.
 * the renderer iterates `MultiDimension.subsets` generically; this const
 * is the registry-side source of truth for the actual key strings, used
 * both when declaring the subsets and when consumers (tests, label
 * resolvers) reference them by name.
 */
export const LotStateSubset = {
	ACTIVE: 'active',
	INACTIVE: 'inactive'
} as const

export type LotStateSubset = (typeof LotStateSubset)[keyof typeof LotStateSubset]

/**
 * one selectable option inside a {@link MultiDimension}.
 *
 * `value` is the BE persistence key for the option (e.g. `'BIDDING'`,
 * `'COMPLETED'`) — the same key that ends up in
 * `FilterProfileConfig.lotStates[value]`. renaming it breaks saved
 * preferences.
 *
 * `labelKey` is a function (not a string) so options can flip their i18n key
 * based on SaaS context or other runtime conditions without rebuilding the
 * registry.
 */
type OptionDescriptor = {
	/** BE persistence key. renaming is a breaking change. */
	value: string
	/** i18n key resolver; function form supports SaaS-conditional labels */
	labelKey: () => string
	/** initial value when no saved preference exists. omitted → `true`. */
	defaultOn?: boolean
}

/**
 * a visual section inside a tab (a labelled block of related filters).
 * groups have no state of their own — they only control rendering order and
 * the section heading shown above their dimensions.
 */
export type GroupDescriptor = {
	/** stable key referenced by {@link BaseDimension.group} */
	key: GroupKey
	/** tab this group renders on */
	tab: FilterTab
	/** i18n key resolver for the section title */
	labelKey: () => string
	/** hide the entire group (and its dimensions) when this returns false. default: visible. FIXME: potentially switch logic to 'hidden' instead of visible */
	visible?: () => boolean
}

type BaseDimension = {
	/**
	 * BE persistence key. used both as the registry id and as the key under
	 * which this dimension is stored in `FilterProfileConfig`. renaming = a
	 * breaking change for saved user preferences.
	 */
	id: string
	tab: FilterTab
	/** references {@link GroupDescriptor.key} */
	group: GroupKey
	/** i18n key for summary aggregation and a11y; per-option labels live on {@link OptionDescriptor} */
	labelKey: () => string
	/** hide the dimension when this returns false. default: visible. FIXME: potentially switch logic to 'hidden' instead of visible */
	visible?: () => boolean
	/**
	 * whether `saveFilterPreference` / `applyConfigToFilterState` should
	 * handle this dimension. some UI-only flags (e.g. transient "modified"
	 * markers) are not persisted.
	 */
	persisted: boolean
	/**
	 * render the dimension as `disabled` and skip it from the persistence
	 * walk. the displayed value is forced from `defaultValue` (boolean) — used
	 * for SaaS-side affordances that exist in the UI for affordance/feedback
	 * but cannot be toggled (e.g. `showRequestedByMM` is always checked for
	 * SaaS).
	 */
	readOnly?: () => boolean
	/**
	 * whether this dimension narrows the result set the user sees on the map.
	 *
	 * - omitted / `true`  — a real filter; counted by `FilterSummary` toward
	 *                       `selected` / `total` / `restrictedDimensions`
	 * - `false`           — pure display/UX toggle (e.g. `groupLots`
	 *                       clusters nearby lots but does not hide any). the
	 *                       summary excludes it entirely, so flipping it
	 *                       does NOT increment "active filters" badges
	 *
	 * defaulting to "affects" is intentional: every filter is a real filter
	 * unless it explicitly opts out, which means new dimensions never
	 * accidentally drop out of the summary.
	 */
	affectsResults?: boolean
}

/** a single on/off toggle. counts 1-of-1 in summary aggregates. */
export type BooleanDimension = BaseDimension & {
	kind: 'boolean'
	/** initial value when no saved preference exists. function form allows SaaS-conditional defaults. */
	defaultValue: () => boolean
}

/**
 * a multi-select dimension (a set of options that can each be on/off).
 * counts selected-of-total in summary aggregates.
 *
 * `allToggle`:
 *  - `'none'`  — no "all" affordance
 *  - `'simple'` — one "All" checkbox that flips every option
 *  - `'split'`  — distinct toggles per {@link MultiDimension.subsets} entry
 *                 (e.g. "All active" / "All inactive" on `lotStates`)
 */
export type MultiDimension = BaseDimension & {
	kind: 'multi'
	options: () => OptionDescriptor[]
	allToggle?: 'none' | 'simple' | 'split'
	/** option `value`s belonging to each named subset. required for `allToggle: 'split'`. */
	subsets?: { [subsetKey: string]: () => string[] }
	/** i18n key resolver for split-toggle row labels (e.g. "Alle aktiv") */
	subsetLabelKey?: (subsetKey: string) => string
}

export type FilterDimension = BooleanDimension | MultiDimension

/**
 * per-dimension entry in a {@link FilterSummary}.
 *
 * `selected` and `total` count the dimension's *leaf options* only:
 *   - boolean dim → total = 1, selected = 0|1
 *   - multi dim   → total = options.length, selected = options on
 * "All" and "All <subset>" toggles are UI affordances that batch-flip the
 * underlying options — they are NOT counted separately.
 */
export type FilterDimensionSummary = {
	id: string
	/** `GroupDescriptor.key` this dimension belongs to — lets consumers regroup the flat list */
	group: string
	labelKey: string
	selected: number
	total: number
	/** `selected < total` — the dimension is currently restricting what's shown */
	isRestricted: boolean
}

/**
 * tab-scoped (or all-tabs) aggregate over every visible, user-controllable
 * dimension. dimensions hidden by `visible()` or marked `readOnly()` are
 * excluded from every field below.
 */
export type FilterSummary = {
	/**
	 * sum of `selected` across all included dimensions — i.e. total leaf
	 * checkboxes currently on (booleans contribute 0|1, multis contribute
	 * the count of options whose state is true). "All" / subset toggles do
	 * not contribute — they are derived UI affordances over the same leaves.
	 */
	selected: number
	/**
	 * sum of `total` across all included dimensions — total leaf checkboxes
	 * the user could theoretically turn on. same exclusion rule as
	 * `selected`: "All" / subset toggles do not contribute.
	 */
	total: number
	/**
	 * count of dimensions where `selected < total`, i.e. dimensions the user
	 * has actively narrowed. a dimension with every option on is not
	 * filtering anything out and does not count. use this for "N filters
	 * active" affordances — it answers "how many dimensions are restricting
	 * the result set" rather than "how many checkboxes are checked".
	 */
	restrictedDimensions: number
	/**
	 * count of dimensions considered by the tally — the denominator for
	 * `restrictedDimensions` ("3 of 7 dimensions are filtering"). hidden
	 * (e.g. SaaS-gated), read-only, and `affectsResults: false` dimensions
	 * are excluded.
	 */
	visibleDimensions: number
	/**
	 * count of groups containing at least one restricted dimension. a
	 * group-level aggregate of `restrictedDimensions` — useful when the UI
	 * counts visual sections rather than individual checkbox-rows.
	 */
	restrictedGroups: number
	/**
	 * count of groups considered by the tally — the denominator for
	 * `restrictedGroups`. a group counts only if it survives its own
	 * `visible()` check AND has at least one dimension that survived all
	 * dimension-level filters (visible / readOnly / affectsResults).
	 */
	visibleGroups: number
	dimensions: FilterDimensionSummary[]
}
